import ls from 'localstorage-slim';
import {
  localStorageKeysConstants,
  SCASessionTypesConstants,
  SCAActionsConstants,
  PERSISTENT_SCA_ACCESS_KEY,
} from '../../constants';

const {FIVE_MINUTES_SESSION, LONG_SESSION, PER_OPERATION_SESSION} = SCASessionTypesConstants;
const {SCA_HEADER_TYPE, SCA_SESSION_KEY} = localStorageKeysConstants;

const getObjProp = (data, key, defaultValue) => data ? data[key] || defaultValue : defaultValue;

const SCA_OPERATION_LS_PREFIX = 'sca-operation';

export const scaHelpers = {
  getOperationType: (operationName) => {
    if (operationName === PERSISTENT_SCA_ACCESS_KEY) return LONG_SESSION;

    // Grouped actions by session type
    const longSessionActions = new Set([
      SCAActionsConstants.OVERVIEW_CHARTS_AND_TABLES_LOADING_ACTION,
      SCAActionsConstants.BALANCE_LOADING,
      SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION,
      SCAActionsConstants.TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION,
    ]);

    const fiveMinutesSessionActions = new Set([
      SCAActionsConstants.ADD_FUNDS_LOADING,
      SCAActionsConstants.TRANSACTIONS_DOWNLOAD_PDF_AND_CSV_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CREATION_WITH_CARD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_BULK_CREATION_WITH_CARD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CARD_RECREATE_ACTION,
    ]);

    const perOperationSessionActions = new Set([
      SCAActionsConstants.SUBSCRIPTION_CARD_CREATION_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CHANGE_CARD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CHANGE_PAYMENT_METHOD_ACTION,
      SCAActionsConstants.SUBSCRIPTION_CHANGE_STATUS_ACTION,
      SCAActionsConstants.DISPLAY_CARD_DETAILS_ACTION,
      SCAActionsConstants.CARD_EDITION_ACTION,
      SCAActionsConstants.LOCK_CARD_ACTION,
      SCAActionsConstants.UNLOCK_CARD_ACTION,
      SCAActionsConstants.DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION,
      SCAActionsConstants.ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION,
    ]);

    // Determine operation type efficiently
    if (longSessionActions.has(operationName)) return LONG_SESSION;
    if (fiveMinutesSessionActions.has(operationName)) return FIVE_MINUTES_SESSION;
    if (perOperationSessionActions.has(operationName)) return PER_OPERATION_SESSION;

    return PER_OPERATION_SESSION; // Default fallback
  },
  clearStoredData: () => {
    Object.values(SCAActionsConstants).forEach(key => ls.remove(`${SCA_OPERATION_LS_PREFIX}-${key}`))
  },
  storeOperationToken: ({headerType, operationName, sessionKey}) => {
    const ttls = {
      [PER_OPERATION_SESSION]: 2,
      [FIVE_MINUTES_SESSION]: 300, // 5 minutes
      [LONG_SESSION]: 7776000 // 90 days
    };
    const operationType = scaHelpers.getOperationType(operationName);
    const ttl = ttls[operationType] || null;

    ls.set(
      `${SCA_OPERATION_LS_PREFIX}-${operationName}`,
      {
        [SCA_HEADER_TYPE]: headerType,
        [SCA_SESSION_KEY]: sessionKey
      },
      { encrypt: true, ttl }
    );
  },
  getOperationToken: (operationName) => ls.get(`${SCA_OPERATION_LS_PREFIX}-${operationName}`, { decrypt: true }),
  getAuthHeaders: (operationName, defaultHeaders) => {
    if (defaultHeaders) return defaultHeaders;
    let token = scaHelpers.getOperationToken(operationName) || '';
    let headers = {
      'sca-header-type': getObjProp(token, SCA_HEADER_TYPE) || 'keyless'
    }
    if (token) {
      headers = {
        ...headers,
        'sca-session-key': getObjProp(token, SCA_SESSION_KEY, '')
      }
    }
    return headers;
  },
  SCAResponseCallback: ({response, scaCallback, errorCallback}) => {
    const statusCode = getObjProp(response.response, 'status') || 408;
    let detail;
    try {
      detail = getObjProp(response.response.data, 'detail');
    } catch {}
    if ((statusCode === 406 && detail === 'Invalid SMS code. Please try again.') || statusCode === 408) {
      scaCallback && scaCallback({isEnrolled: true, open: true});
    } else if (statusCode === 406) {
      scaCallback && scaCallback({isEnrolled: false, open: true});
    } else {
      errorCallback && errorCallback(response);
    }
  },
  getAuthModalProps: ({action, t}) => {
    const transactionTitle = `${t('main:transactions')} ${t('main:loading')}`
    const title = {
      [SCAActionsConstants.TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION]: transactionTitle,
      [SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION]: transactionTitle,
    }[action] || undefined;
    return { title };
  }
}
